/**
 * Benefits Section with GSAP ScrollTrigger
 * Handles 4-state transitions on desktop with pinned scrolling
 * Mobile: Sticky container with states that change on scroll
 */

"use strict";

function initBenefitsMobileSection() {
  // Mobile section now uses relative positioning - all states visible
  // No observer or scroll tracking needed
      return;
}

function initBenefitsSection() {
  const section = document.querySelector(
    '.benefits-desktop[id^="benefits-section-"]'
  );
  if (!section) return;

  const isMobile = window.innerWidth <= 999;

  // On mobile, don't initialize scroll trigger (we show separate sections)
  if (isMobile) {
    return;
  }

  // Get all elements by data-state
  const title = section.querySelector(".benefits-title");
  const textWrapper = section.querySelector(
    ".benefits-text[data-state='2-3-4']"
  );
  const textTrack = section.querySelector(".benefits-text-track");
  const textState2 = section.querySelector(
    ".benefits-text-state[data-state-index='2']"
  );
  const textState3 = section.querySelector(
    ".benefits-text-state[data-state-index='3']"
  );
  const textState4 = section.querySelector(
    ".benefits-text-state[data-state-index='4']"
  );
  const middleImages = section.querySelectorAll(".benefits-image-wrapper img");

  // Wait for GSAP to be available
  if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") {
    setTimeout(initBenefitsSection, 100);
    return;
  }

  // Register ScrollTrigger plugin
  gsap.registerPlugin(ScrollTrigger);

  // Set initial state (State 1)
  function setInitialState() {
    // Title: State 1 - large font size
    if (title) {
      title.classList.remove("font-size-small");
      title.classList.add("font-size-large");
      gsap.set(title, {
        opacity: 1,
        visibility: "visible",
      });
    }

    // Text wrapper: hidden in state 1
    if (textWrapper) {
      gsap.set(textWrapper, { opacity: 0, visibility: "hidden" });
    }

    // Text track: start at translateY(0)
    if (textTrack) {
      gsap.set(textTrack, { transform: "translateY(0%)" });
    }

    // Text states: all at 0.3 opacity initially (will be set correctly in state 2)
    if (textState2) {
      gsap.set(textState2, { opacity: 0.3 });
    }
    if (textState3) {
      gsap.set(textState3, { opacity: 0.3 });
    }
    if (textState4) {
      gsap.set(textState4, { opacity: 0.3 });
    }

    // Middle images: show state 1, hide others
    middleImages.forEach((img) => {
      const state = img.getAttribute("data-state");
      if (state === "1") {
        gsap.set(img, { opacity: 1, visibility: "visible", zIndex: 1 });
      } else {
        gsap.set(img, { opacity: 0, visibility: "hidden", zIndex: 0 });
      }
    });
  }

  // Initialize state 1
  setInitialState();
  // Progress bar removed
  let progressBar = null;

  // Create ScrollTrigger for state transitions
  ScrollTrigger.create({
    trigger: section,
    start: "top top",
    end: "+=400%", // 4x viewport height for 4 states
    pin: true,
    anticipatePin: 1,
    onUpdate: (self) => {
      const progress = self.progress;

      // Define transition points for 4 states with hold periods
      const state1End = 0.25; // State 1 → State 2 transition ends at 25%
      const state2HoldStart = 0.25; // State 2 hold period starts at 25%
      const state2HoldEnd = 0.4; // State 2 hold period ends at 40%
      const state3TransitionStart = 0.4; // State 2 → State 3 transition starts at 40%
      const state3End = 0.65; // State 2 → State 3 transition ends at 65%
      const state3HoldStart = 0.65; // State 3 hold period starts at 65%
      const state3HoldEnd = 0.8; // State 3 hold period ends at 80%
      const state4TransitionStart = 0.8; // State 3 → State 4 transition starts at 80%

      // Calculate values for each state
      let titleFontSize;
      let textWrapperOpacity, textWrapperVisibility;
      let textTrackY; // Text track translateY value
      let textState2Opacity, textState3Opacity, textState4Opacity;
      let image1Opacity, image2Opacity, image3Opacity, image4Opacity;
      let image1ZIndex, image2ZIndex, image3ZIndex, image4ZIndex;

      if (progress <= state1End) {
        // State 1 → State 2: Title font size changes, text wrapper appears, text track moves
        const state1Progress = progress / state1End;

        // Title: transition from large to small font size
        const largeFontSize = "clamp(2.6rem, -1.4217rem + 6.4348vw, 6.3rem)";
        const smallFontSize = "clamp(1.5rem, -0.5652rem + 3.3043vw, 3.4rem)";
        // We'll handle this with CSS class or inline style

        // Text wrapper: fade in
        textWrapperOpacity = state1Progress;
        textWrapperVisibility = state1Progress > 0 ? "visible" : "hidden";

        // Text track: stay at 0% (state 2)
        textTrackY = 0;

        // Text states: state 2 becomes active (opacity 1), others stay 0.3
        textState2Opacity = 0.3 + state1Progress * 0.7; // 0.3 to 1
        textState3Opacity = 0.3;
        textState4Opacity = 0.3;

        // Images: state 1 fades out, state 2 fades in
        image1Opacity = 1 - state1Progress;
        image2Opacity = state1Progress;
        image3Opacity = 0;
        image4Opacity = 0;
        image1ZIndex = image1Opacity > 0 ? 1 : 0;
        image2ZIndex = image2Opacity > 0 ? 2 : 0;
        image3ZIndex = 0;
        image4ZIndex = 0;
      } else if (progress <= state2HoldEnd) {
        // State 2: Hold period
        textWrapperOpacity = 1;
        textWrapperVisibility = "visible";
        textTrackY = 0;
        textState2Opacity = 1;
        textState3Opacity = 0.3;
        textState4Opacity = 0.3;
        image1Opacity = 0;
        image2Opacity = 1;
        image3Opacity = 0;
        image4Opacity = 0;
        image1ZIndex = 0;
        image2ZIndex = 2;
        image3ZIndex = 0;
        image4ZIndex = 0;
      } else if (progress <= state3End) {
        // State 2 → State 3: Text track moves from 0% to -100%
        const state2Progress =
          (progress - state2HoldEnd) / (state3End - state2HoldEnd);

        textWrapperOpacity = 1;
        textWrapperVisibility = "visible";
        textTrackY = 0 - state2Progress * 100; // 0% to -100%

        // Text states: state 2 fades to 0.3, state 3 fades to 1
        textState2Opacity = 1 - state2Progress * 0.7; // 1 to 0.3
        textState3Opacity = 0.3 + state2Progress * 0.7; // 0.3 to 1
        textState4Opacity = 0.3;

        // Images: state 2 fades out, state 3 fades in
        image1Opacity = 0;
        image2Opacity = 1 - state2Progress;
        image3Opacity = state2Progress;
        image4Opacity = 0;
        image1ZIndex = 0;
        image2ZIndex = image2Opacity > 0 ? 2 : 0;
        image3ZIndex = image3Opacity > 0 ? 3 : 0;
        image4ZIndex = 0;
      } else if (progress <= state3HoldEnd) {
        // State 3: Hold period
        textWrapperOpacity = 1;
        textWrapperVisibility = "visible";
        textTrackY = -100;
        textState2Opacity = 0.3;
        textState3Opacity = 1;
        textState4Opacity = 0.3;
        image1Opacity = 0;
        image2Opacity = 0;
        image3Opacity = 1;
        image4Opacity = 0;
        image1ZIndex = 0;
        image2ZIndex = 0;
        image3ZIndex = 3;
        image4ZIndex = 0;
      } else {
        // State 3 → State 4: Text track moves from -100% to -200%
        const state3Progress = (progress - state3HoldEnd) / (1 - state3HoldEnd);

        textWrapperOpacity = 1;
        textWrapperVisibility = "visible";
        textTrackY = -100 - state3Progress * 100; // -100% to -200%

        // Text states: state 3 fades to 0.3, state 4 fades to 1
        textState2Opacity = 0.3;
        textState3Opacity = 1 - state3Progress * 0.7; // 1 to 0.3
        textState4Opacity = 0.3 + state3Progress * 0.7; // 0.3 to 1

        // Images: state 3 fades out, state 4 fades in
        image1Opacity = 0;
        image2Opacity = 0;
        image3Opacity = 1 - state3Progress;
        image4Opacity = state3Progress;
        image1ZIndex = 0;
        image2ZIndex = 0;
        image3ZIndex = image3Opacity > 0 ? 3 : 0;
        image4ZIndex = image4Opacity > 0 ? 4 : 0;
      }

      // Update title font size using CSS classes
      if (title) {
        if (progress <= state1End) {
          // Transitioning from large to small during state 1 → 2
          const state1Progress = progress / state1End;
          // Switch to small font size class when transitioning
          if (state1Progress > 0.5) {
            title.classList.remove("font-size-large");
            title.classList.add("font-size-small");
          } else {
            // Restore large font size when scrolling back
            title.classList.remove("font-size-small");
            title.classList.add("font-size-large");
          }
        } else {
          // Small font size for states 2-3-4
          title.classList.remove("font-size-large");
          title.classList.add("font-size-small");
        }
      }

      // Update text wrapper visibility
      if (textWrapper) {
        gsap.to(textWrapper, {
          opacity: textWrapperOpacity,
          visibility: textWrapperVisibility,
          duration: 0.1,
          ease: "none",
        });
      }

      // Update text track
      if (textTrack) {
        gsap.to(textTrack, {
          transform: `translateY(${textTrackY}%)`,
          duration: 0.1,
          ease: "none",
        });
      }

      // Update text state opacities
      if (textState2) {
        gsap.to(textState2, {
          opacity: textState2Opacity,
          duration: 0.1,
          ease: "none",
        });
      }
      if (textState3) {
        gsap.to(textState3, {
          opacity: textState3Opacity,
          duration: 0.1,
          ease: "none",
        });
      }
      if (textState4) {
        gsap.to(textState4, {
          opacity: textState4Opacity,
          duration: 0.1,
          ease: "none",
        });
      }

      // Update middle images
      middleImages.forEach((img) => {
        const state = img.getAttribute("data-state");
        let targetOpacity = 0;
        let targetVisibility = "hidden";
        let targetZIndex = 0;

        if (state === "1") {
          targetOpacity = image1Opacity;
          targetVisibility = image1Opacity > 0 ? "visible" : "hidden";
          targetZIndex = image1ZIndex;
        } else if (state === "2") {
          targetOpacity = image2Opacity;
          targetVisibility = image2Opacity > 0 ? "visible" : "hidden";
          targetZIndex = image2ZIndex;
        } else if (state === "3") {
          targetOpacity = image3Opacity;
          targetVisibility = image3Opacity > 0 ? "visible" : "hidden";
          targetZIndex = image3ZIndex;
        } else if (state === "4") {
          targetOpacity = image4Opacity;
          targetVisibility = image4Opacity > 0 ? "visible" : "hidden";
          targetZIndex = image4ZIndex;
        }

        gsap.to(img, {
          opacity: targetOpacity,
          visibility: targetVisibility,
          zIndex: targetZIndex,
          duration: 0.1,
          ease: "none",
        });
      });

      // Progress bar removed
    },
  });

  // Handle window resize
  let resizeTimer;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      ScrollTrigger.refresh();
      if (window.innerWidth <= 999) {
        // Mobile: no initialization needed
      } else {
        // Desktop: reinitialize
        ScrollTrigger.getAll().forEach((trigger) => {
          if (trigger.trigger === section) {
            trigger.kill();
          }
        });
        initBenefitsSection();
      }
    }, 250);
  });
}

// Initialize when DOM is ready
function initialize() {
  // Use global scroll-to-top function if available, otherwise execute immediately
  if (typeof window.ensureScrollToTop === "function") {
    window.ensureScrollToTop(() => {
      initBenefitsSection();
      initBenefitsMobileSection();
    });
  } else {
    // Fallback: wait a bit for global.js to load
    setTimeout(() => {
      if (typeof window.ensureScrollToTop === "function") {
        window.ensureScrollToTop(() => {
          initBenefitsSection();
          initBenefitsMobileSection();
        });
      } else {
        initBenefitsSection();
        initBenefitsMobileSection();
      }
    }, 100);
  }
}

// Wait for DOM to be ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initialize);
} else {
  initialize();
}

// Handle browser back/forward navigation
window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    if (typeof window.ensureScrollToTop === "function") {
      window.ensureScrollToTop(() => {
        const section = document.querySelector(
          '.benefits-desktop[id^="benefits-section-"]'
        );
        if (section && typeof ScrollTrigger !== "undefined") {
          ScrollTrigger.getAll().forEach((trigger) => {
            if (trigger.trigger === section) {
              trigger.kill();
            }
          });
        }
        initBenefitsSection();
        initBenefitsMobileSection();
      });
    } else {
      const section = document.querySelector(
        '.benefits-desktop[id^="benefits-section-"]'
      );
      if (section && typeof ScrollTrigger !== "undefined") {
        ScrollTrigger.getAll().forEach((trigger) => {
          if (trigger.trigger === section) {
            trigger.kill();
          }
        });
      }
      initBenefitsSection();
      initBenefitsMobileSection();
    }
  }
});
